import { formatters, useTranslation } from "@equiem/web-ng-lib";
import { notNullOrUndefined, Site } from "@equiem/web-ng-lib";
import React, { useContext } from "react";
import { CommentButton, LikeButton, RegisterNow, RsvpButton } from ".";
import { PublicContentViewQuery } from "../../generated/gateway-client";
import { arrayNotEmpty } from "../../lib/arrayNotEmpty";
import { ContentType } from "../../lib/ContentType";
import { DateTime } from "../../lib/DateTime";
import { ContentNotAvailable } from "../ContentNotAvailable";
import { Calendar } from "../icons/Calendar";
import { Clock } from "../icons/Clock";
import { Location } from "../icons/Location";
import { PostContent } from "../PostContent";
import { PostContentPage } from "../PostContentPage";

type Occurrence = PublicContentViewQuery["publicContentView"]["node"] & {
  __typename: "Occurrence";
};

interface Props {
  post: Occurrence;
}

export const EventOccurrence: React.FC<Props> = ({ post }) => {
  const { t, i18n } = useTranslation();

  if (post.eventPost == null) {
    return <ContentNotAvailable title={t("main.noEventPost")} />;
  }

  const { timezone } = useContext(Site);
  const { eventPost } = post;
  const startDate = DateTime.fromMillis(post.startDate, {
    zone: timezone,
  });
  const endDate = DateTime.fromMillis(post.endDate, { zone: timezone });
  const sameDate = startDate.hasSame(endDate, "date");
  const categories = eventPost.categories.map((cat) => cat.label);

  const actions = (
    <>
      {!post.isRsvpEnabled ? null : (
        <RsvpButton label={post.hasCapacity ? t("main.rsvp") : t("main.eventFull")} />
      )}
      <LikeButton
        shadow="always"
        className="ml-2 mb-2"
        contentType={ContentType.EventPost}
        contentUuid={post.uuid}
      />
      <CommentButton />
    </>
  );

  const author =
    eventPost.author != null
      ? {
          name: eventPost.author.displayName,
          avatar: eventPost.author.avatar,
        }
      : undefined;

  return (
    <>
      <PostContentPage
        uuid={post.uuid}
        contentType={ContentType.EventPost}
        title={post.title}
        images={eventPost.images.map((i) => i.url).filter(notNullOrUndefined)}
        author={author}
        publishDate={DateTime.fromMillis(eventPost.publishDate, {
          zone: timezone,
        })}
        actions={actions}
        commentsEnabled={false}
        enableEntityViewMetric={false}
        pageMetricName="anonymous_page_view"
        categories={categories}
        relatedInformation={
          arrayNotEmpty(eventPost.relatedInfo)
            ? eventPost.relatedInfo
            : undefined
        }
      >
        <div className="my-3 details">
          <div className="date my-1">
            <Calendar size={22} />
            <span className="pl-1 icon-label">
              {sameDate
                ? formatters.dateshort(post.startDate, i18n.language, { timezone })
                : `${formatters.dateshort(post.startDate, i18n.language, { timezone })} - ${formatters.dateshort(post.endDate, i18n.language, { timezone })}`}
            </span>
          </div>
          <div className="time my-1">
            <Clock size={22} />
            <span className="pl-1 icon-label">
              {formatters.timeshort(post.startDate, i18n.language, { timezone })} - {formatters.timeshort(post.endDate, i18n.language, { timezone })}
            </span>
          </div>
          {post.location != null ? (
            <div className="location my-1">
              <Location size={22} />
              <span className="icon-label">
                {`${post.location} `}
                {post.eventPost?.address != null ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      post.eventPost.address,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pl-3"
                  >
                    <u>{t("main.viewOnMap")}</u>
                  </a>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
        <PostContent html={eventPost.body} />
      </PostContentPage>
      <RegisterNow />
    </>
  );
};
